'use client'

export const API_PREFIX = '/api'

const AUTH_TOKEN_KEY = 'auth-token'
const AUTH_USER_KEY = 'auth-user'
const AUTH_REFRESH_TOKEN_KEY = 'auth-refresh-token'
/** 同一页面内会话被续期或清除时派发（storage 事件只通知其它标签页）。 */
export const AUTH_SESSION_EVENT = 'auth-session-changed'

export type AuthUser = {
  username: string
  name?: string
  email?: string
  role?: string
  department?: string
  [key: string]: unknown
}

export type AuthSession = {
  token: string
  user: AuthUser
  /** 30 天 refresh token；旧版登录没有它，access token 过期后只能重新登录。 */
  refreshToken?: string
}

export class ApiError extends Error {
  status: number
  statusText: string
  data: unknown
  response: Response

  constructor(message: string, response: Response, data: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = response.status
    this.statusText = response.statusText
    this.data = data
    this.response = response
  }
}

type ApiJsonInit = Omit<RequestInit, 'body'> & {
  body?: BodyInit | null
  json?: unknown
}

function hasStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

export function getAuthToken() {
  if (!hasStorage()) return null
  try {
    return window.localStorage.getItem(AUTH_TOKEN_KEY)
  } catch {
    return null
  }
}

export function readAuthSession(): AuthSession | null {
  if (!hasStorage()) return null
  try {
    const token = window.localStorage.getItem(AUTH_TOKEN_KEY)
    const rawUser = window.localStorage.getItem(AUTH_USER_KEY)
    if (!token || !rawUser) return null
    const user = JSON.parse(rawUser) as AuthUser
    if (!user?.username) return null
    return { token, user }
  } catch {
    clearAuthSession()
    return null
  }
}

export function getRefreshToken() {
  if (!hasStorage()) return null
  try {
    return window.localStorage.getItem(AUTH_REFRESH_TOKEN_KEY)
  } catch {
    return null
  }
}

function notifySessionChanged() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(AUTH_SESSION_EVENT))
}

export function storeAuthSession(session: AuthSession) {
  if (!hasStorage()) return
  window.localStorage.setItem(AUTH_TOKEN_KEY, session.token)
  window.localStorage.setItem(AUTH_USER_KEY, JSON.stringify(session.user))
  if (session.refreshToken)
    window.localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, session.refreshToken)
  else window.localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY)
}

export function clearAuthSession() {
  if (!hasStorage()) return
  window.localStorage.removeItem(AUTH_TOKEN_KEY)
  window.localStorage.removeItem(AUTH_USER_KEY)
  window.localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY)
}

function sameOriginBase() {
  if (typeof window !== 'undefined') {
    return window.location.origin
  }
  return 'http://localhost'
}

function isSameOriginApiUrl(url: URL) {
  return url.origin === sameOriginBase() && url.pathname.startsWith(API_PREFIX)
}

export function buildApiUrl(input: string | URL) {
  const raw = input.toString()
  if (raw.startsWith(API_PREFIX)) {
    return raw
  }
  if (raw.startsWith('/')) {
    return raw.startsWith(`${API_PREFIX}/`) ? raw : `${API_PREFIX}${raw}`
  }

  const url = new URL(raw, sameOriginBase())
  if (isSameOriginApiUrl(url)) {
    return `${url.pathname}${url.search}${url.hash}`
  }
  return url.toString()
}

function requestIsSameOriginApi(input: RequestInfo | URL) {
  if (input instanceof Request) {
    return isSameOriginApiUrl(new URL(input.url, sameOriginBase()))
  }
  const url = new URL(buildApiUrl(input), sameOriginBase())
  return isSameOriginApiUrl(url)
}

function normalizeRequestInput(input: RequestInfo | URL) {
  if (input instanceof Request) {
    return input
  }
  return buildApiUrl(input)
}

type SessionResponse = { token?: string; refreshToken?: string; user?: AuthUser }

let refreshInFlight: Promise<string | null> | null = null

/**
 * 用 refresh token 换新的 access token，返回新 token；无法续期返回 null。
 *
 * 必须串行：后端每次续期都会轮换 refresh token，并把旧值的重放视为泄露而撤销整个会话，
 * 所以同一标签页内的并发 401 共用一个请求，跨标签页用 Web Locks 排队，
 * 排到时若 access token 已被别处换过就直接复用。
 */
export function refreshAccessToken(staleToken: string | null): Promise<string | null> {
  refreshInFlight ??= withRefreshLock(() => performRefresh(staleToken)).finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

function withRefreshLock(task: () => Promise<string | null>): Promise<string | null> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  // lib.dom 把回调的 Promise 返回值类型标成 Promise<Promise<T>>；运行时已展开，这里用 then 拍平类型。
  return locks ? locks.request('auth-refresh', task).then((token) => token) : task()
}

async function performRefresh(staleToken: string | null): Promise<string | null> {
  const current = getAuthToken()
  if (current && current !== staleToken) return current

  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    // 旧版登录没有 refresh token：access token 已失效，只能重新登录。
    expireSession()
    return null
  }
  let response: Response
  try {
    response = await fetch(`${API_PREFIX}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
  } catch {
    // 网络失败不代表会话失效，保留登录态，让本次请求按原 401 处理。
    return null
  }
  const data = (await response.json().catch(() => null)) as SessionResponse | null
  if (!response.ok || !data?.token || !data.user) {
    if (response.status === 401 || response.status === 400) expireSession()
    return null
  }
  storeAuthSession({ token: data.token, user: data.user, refreshToken: data.refreshToken })
  notifySessionChanged()
  return data.token
}

function expireSession() {
  clearAuthSession()
  notifySessionChanged()
}

function isAuthEndpoint(input: RequestInfo | URL) {
  const url = new URL(input instanceof Request ? input.url : buildApiUrl(input), sameOriginBase())
  return /^\/api\/auth\/(login|refresh|logout|register)$/.test(url.pathname)
}

export async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const headers = new Headers(init.headers)
  let sentToken: string | null = null
  if (requestIsSameOriginApi(input) && !headers.has('Authorization')) {
    sentToken = getAuthToken()
    if (sentToken) {
      headers.set('Authorization', `Bearer ${sentToken}`)
    }
  }

  const response = await fetch(normalizeRequestInput(input), {
    ...init,
    headers,
  })

  // 只处理本函数自动附带的 token；调用方自带 Authorization、Request 对象（body 不可重放）
  // 和认证端点本身都不自动续期。
  if (
    response.status !== 401 ||
    !sentToken ||
    input instanceof Request ||
    isAuthEndpoint(input) ||
    init.signal?.aborted
  ) {
    return response
  }
  const nextToken = await refreshAccessToken(sentToken)
  if (!nextToken) return response
  headers.set('Authorization', `Bearer ${nextToken}`)
  return fetch(normalizeRequestInput(input), { ...init, headers })
}

async function parseResponseBody(response: Response) {
  const raw = await response.text().catch(() => '')
  if (!raw.trim()) return null

  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    return JSON.parse(raw)
  }

  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

function getErrorMessage(data: unknown, fallback: string) {
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>
    const message = record.error || record.message
    if (typeof message === 'string' && message.trim()) {
      return message
    }
  }
  if (typeof data === 'string' && data.trim()) {
    return data
  }
  return fallback
}

export async function apiJson<T = unknown>(input: RequestInfo | URL, init: ApiJsonInit = {}) {
  const { json, ...requestInit } = init
  const headers = new Headers(requestInit.headers)
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json')
  }

  const body = json === undefined ? requestInit.body : JSON.stringify(json)
  if (json !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await apiFetch(input, {
    ...requestInit,
    headers,
    body,
  })
  const data = await parseResponseBody(response)
  if (!response.ok) {
    throw new ApiError(
      getErrorMessage(data, `Request failed with status ${response.status}`),
      response,
      data
    )
  }
  return data as T
}

export function buildApiWebSocketUrl(input: string | URL) {
  const url = new URL(buildApiUrl(input), sameOriginBase())
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return url.toString()
}
